import { UserButton, useClerk } from '@clerk/clerk-react';
import {
  Activity as ActivityIcon,
  Building,
  Calendar,
  CalendarClock,
  CheckSquare,
  ClipboardList,
  Gauge,
  LayoutDashboard,
  LogOut,
  Map as MapIcon,
  MessageSquareWarning,
  Radio,
  Receipt,
  Route as RouteIcon,
  Settings,
  ShieldAlert,
  Users,
  Users2,
} from 'lucide-react';
import { NavLink, Outlet } from 'react-router-dom';
import { useLiveEvents } from '../api/useLiveEvents.js';
import { useMe } from '../api/useMe.js';
import { cn } from '../lib/cn.js';
import { RoleGate } from './RoleGate.js';
import { TestAlarmButton } from './TestAlarmButton.js';

// Monitoring-centre shell. Realigned to the Empower Safety brand console design:
// white sidebar with brand-purple accent on active items, ink-hierarchy text,
// JetBrains Mono eyebrows above section labels.
//
// Role gate: dashboard access is limited to `monitor` and `admin` app roles. A
// worker who signs in here sees an access-denied screen rather than empty lists.

interface NavItem {
  label: string;
  to: string;
  icon: typeof LayoutDashboard;
}

const opsNav: NavItem[] = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard },
  { label: 'Alarms', to: '/alarms', icon: ShieldAlert },
  { label: 'Live map', to: '/map', icon: MapIcon },
  { label: 'Check-ins', to: '/check-ins', icon: CheckSquare },
  { label: 'Journeys', to: '/journeys', icon: RouteIcon },
  { label: 'Activity', to: '/activity', icon: ActivityIcon },
];

const fieldNav: NavItem[] = [
  { label: 'Visits', to: '/visits', icon: Calendar },
  { label: 'Users', to: '/users', icon: Users },
  { label: 'Teams', to: '/teams', icon: Users2 },
  { label: 'Sites', to: '/sites', icon: Building },
];

const opsManageNav: NavItem[] = [
  { label: 'Incidents', to: '/incidents', icon: ClipboardList },
  { label: 'Broadcasts', to: '/broadcasts', icon: Radio },
];

// Reports section. Visible to monitor / admin / teamLeader. The first item
// is the Travel report (Phase 3); future reports (check-in, alarm,
// utilisation) slot in here.
const reportsNav: NavItem[] = [
  { label: 'Travel', to: '/reports/travel', icon: RouteIcon },
  { label: 'Reimbursement', to: '/reports/reimbursement', icon: Receipt },
  { label: 'Check-ins', to: '/reports/check-ins', icon: CheckSquare },
  { label: 'Alarms', to: '/reports/alarms', icon: ShieldAlert },
  { label: 'Utilisation', to: '/reports/utilisation', icon: Gauge },
  { label: 'Off-shift visits', to: '/reports/off-shift-visits', icon: CalendarClock },
];

const secondaryNav: NavItem[] = [{ label: 'Settings', to: '/settings', icon: Settings }];

export function Shell() {
  const { signOut } = useClerk();
  const { data: me } = useMe();
  const live = useLiveEvents();

  const initials = (me?.display_name ?? '')
    .split(' ')
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2);

  return (
    <RoleGate allow={['monitor', 'admin']}>
      <div className="flex h-screen w-screen overflow-hidden bg-surface-0">
        {/* Sidebar — white, with subtle hairline + active-state purple accent */}
        <aside className="relative flex w-60 shrink-0 flex-col overflow-hidden border-r border-line bg-white">
          {/* Brand block */}
          <div className="border-b border-line px-5 pt-5 pb-4">
            <div className="flex items-baseline gap-1.5">
              <span className="font-heading text-[26px] font-extrabold leading-none tracking-[-0.04em] text-brand-purple lowercase">
                empower
              </span>
            </div>
            <span className="mt-1 block font-heading text-[9px] font-bold uppercase tracking-[0.32em] text-ink-dim">
              Safety · Monitoring
            </span>
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-light-blue-15 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-brand-purple">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-cyan" />
              Console
            </span>
          </div>

          <nav className="flex flex-1 flex-col gap-3 overflow-y-auto px-3 py-3">
            <NavSection label="Operations" items={opsNav} />
            <NavSection label="Field" items={fieldNav} />
            <NavSection label="Manage" items={opsManageNav} />
            {/* Reports show for anyone with monitor/admin capability OR a
                team leader assignment. Workers without those see no Reports
                section at all. */}
            {(me?.capabilities?.monitor ||
              me?.capabilities?.admin ||
              me?.capabilities?.teamLeader) && <NavSection label="Reports" items={reportsNav} />}
          </nav>

          {/* Footer: user card + secondary nav */}
          <div className="border-t border-line px-3 py-3">
            <div className="flex flex-col gap-1">
              {secondaryNav.map((item) => (
                <SidebarLink key={item.to} {...item} />
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2.5 rounded-md border border-line bg-surface-2 px-2.5 py-2">
              <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-gradient font-heading text-[11px] font-bold text-white">
                {initials || '··'}
              </div>
              <div className="min-w-0 flex-1 text-[13px]">
                <div className="truncate font-semibold text-ink">
                  {me?.display_name ?? 'Loading…'}
                </div>
                <div className="font-mono text-[9.5px] font-medium uppercase tracking-wider text-ink-mute">
                  {me?.app_role ?? 'role'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => signOut()}
                className="rounded-md p-1.5 text-ink-faint hover:bg-surface-3 hover:text-ink"
                title="Sign out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
          </div>
        </aside>

        {/* Main column — vitals strip + content */}
        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 items-center justify-between gap-4 border-b border-line bg-white px-6">
            <div className="flex flex-col leading-tight">
              <div className="text-[13px] font-medium text-ink">
                {me?.display_name ? `Signed in as ${me.display_name}` : 'Loading session…'}
              </div>
              <div className="flex items-center gap-2 text-[11px] text-ink-faint">
                <span className="font-mono uppercase tracking-wider">
                  {me?.organisation_id
                    ? `org · ${me.organisation_id.slice(0, 8)}`
                    : 'no active organisation'}
                </span>
                <LiveDot status={live.status} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <TestAlarmButton />
              <UserButton afterSignOutUrl="/" />
            </div>
          </header>

          <main className="flex-1 overflow-y-auto px-7 py-6">
            <Outlet />
          </main>
        </div>
      </div>
    </RoleGate>
  );
}

function NavSection({ label, items }: { label: string; items: NavItem[] }) {
  return (
    <div className="flex flex-col gap-px">
      <span className="nav-section-label">{label}</span>
      {items.map((item) => (
        <SidebarLink key={item.to} {...item} />
      ))}
    </div>
  );
}

function SidebarLink({ to, label, icon: Icon }: NavItem) {
  return (
    <NavLink
      to={to}
      end={to === '/'}
      className={({ isActive }) => cn('nav-item', isActive && 'nav-item-active')}
    >
      {({ isActive }) => (
        <>
          <Icon className={cn('h-4 w-4 shrink-0 opacity-80', isActive && 'opacity-100')} />
          <span className="truncate">{label}</span>
        </>
      )}
    </NavLink>
  );
}

function LiveDot({ status }: { status: 'idle' | 'connecting' | 'open' | 'closed' | 'error' }) {
  const label =
    status === 'open'
      ? 'Live'
      : status === 'connecting'
        ? 'Connecting…'
        : status === 'error' || status === 'closed'
          ? 'Reconnecting…'
          : 'Idle';
  const dot =
    status === 'open'
      ? 'bg-success animate-live-pulse'
      : status === 'connecting'
        ? 'bg-warning animate-pulse'
        : 'bg-ink-mute';
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-3 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
      <span className={cn('h-1.5 w-1.5 rounded-full', dot)} />
      {label}
    </span>
  );
}

// Kept to avoid unused-import noise on the lucide barrel; referenced elsewhere if needed.
export const _iconBarrel = MessageSquareWarning;
